import React, { useState } from "react";
import { HelpCircle, ChevronDown, ChevronUp, Clock, ShieldCheck, Mail } from "lucide-react";

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "What does Precision Home Leads LLC do?",
      a: "Precision Home Leads LLC is a specialized business-to-business lead generation and digital marketing company. We run compliant consumer search and advertising campaigns to find homeowners who need home improvement projects. Once qualified, we immediately routing those inquiries exclusively to local home service contractors."
    },
    {
      q: "How are leads generated?",
      a: "All leads are generated organically and through paid search placement on search engines. Consumers looking for local contracting professionals visit our marketing websites and voluntarily submit request forms specifying project scopes, contact coordinates, and geographic zip codes. We do not scraping directories or purchase cold lists."
    },
    {
      q: "What is considered a qualified lead?",
      a: "A qualified lead is an inquiry submitted by a genuine consumer who has verified home ownership (or authority to modify properties), resides in your geographic coverage, has selected a trade scope we support, and has verified contact details (phone and email checked through automated scrubbing systems)."
    },
    {
      q: "How are leads delivered?",
      a: "Leads are transmitted in real-time as soon as the homeowner's request is verified. You will receive an instant push notification via SMS/text message and a comprehensive detail block via email. If needed, we can also integrate with your CRM to push leads directly."
    },
    {
      q: "What happens if a lead is invalid?",
      a: "Although we pre-screen all queries, occasionally an invalid lead (e.g. disconnected phone number, wrong trade selection) can occur. Precision Home Leads LLC provides a case-by-case replacement program. You can file a dispute with our professional support desk within 7 days of delivery, and our account managers will issue a replacement credit if verified."
    },
    {
      q: "How do I contact support?",
      a: "Our customer success managers are available Monday through Saturday from 8:00 AM to 7:00 PM CST. You can contact support directly via telephone at +1 (470) 765-0187 or email us at contact@precisionleads.co. We review all administrative inquiries within 24 business hours."
    },
    {
      q: "Can I cancel service?",
      a: "Yes. You may request to pause or cancel future campaign services at any time. Because we generate actual digital assets and qualified leads, all leads delivered or services performed up to the cancellation time are non-refundable. pausing campaigns stops future billings instantly. View our Cancellation Policy page for detailed terms."
    }
  ];

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-white" data-testid="faq-page">
      
      {/* Page Header */}
      <section className="bg-slate-900 text-white py-16 md:py-24 text-center space-y-4">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 max-w-3xl space-y-3">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400">Frequently Asked Questions</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
            Transparent Answers About Our Lead Process
          </h1>
          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            Everything you need to know about our U.S. home service lead campaigns, validation systems, billing compliance, and contractor support.
          </p>
        </div>
      </section>

      {/* Accordion List */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          
          <div className="space-y-4" data-testid="faq-accordion-container">
            {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div 
                  key={i} 
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden transition-all duration-150"
                  data-testid={`faq-item-${i}`}
                >
                  {/* Question Toggle Header */}
                  <button
                    onClick={() => handleToggle(i)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 focus:outline-none transition-colors"
                    data-testid={`faq-toggle-button-${i}`}
                    aria-expanded={isOpen}
                  >
                    <span className="font-semibold text-slate-900 text-sm sm:text-base pr-4">
                      {faq.q}
                    </span>
                    {isOpen ? (
                      <ChevronUp className="h-5 w-5 text-emerald-600 shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
                    )}
                  </button>

                  {/* Answer Block */}
                  {isOpen && (
                    <div 
                      className="p-6 pt-0 border-t border-slate-100 bg-slate-50/50 animate-in fade-in slide-in-from-top-2 duration-150"
                      data-testid={`faq-answer-block-${i}`}
                    >
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed pt-4">
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Underwriting Trust footer */}
          <div className="mt-16 bg-slate-50 border border-slate-200 p-8 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <HelpCircle className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-slate-900 text-sm">Have subsequent billing questions?</h4>
                <p className="text-xs text-slate-500 max-w-md leading-relaxed">
                  We supply fully documented invoice transaction sheets on request to satisfy corporate risk assessments and payment auditing.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 shrink-0">
              <Mail className="h-4 w-4 text-emerald-600" />
              <a href="mailto:contact@precisionleads.co" className="text-xs font-semibold text-slate-900 hover:text-emerald-600 transition-colors">
                contact@precisionleads.co
              </a>
            </div>
          </div>

        </div>
      </section>

    </div>
  );
}
